import { useEffect, useRef } from 'react'

import type { Cue, Track } from '../sound/cues.js'
import { useSound } from '../sound/sound-context.js'

/**
 * Turning what a game's view says into what it sounds like.
 *
 * Everything here reacts to the **view changing**, never to a reducer. A cue
 * fired inside `packages/shared` would fire again every time a round is
 * replayed — and rounds are replayed, server-side, to be scored — silently, on
 * a machine with no speakers. That is a bug found only by wondering.
 *
 * Nothing here says anything the screen does not. Every cue marks a moment
 * already announced in words, in a live region, so a muted game loses
 * atmosphere and not one fact.
 */

/** The loop that plays while this screen is up, and stops when it is not. */
export function useRoundMusic(track: Track): void {
  const sound = useSound()

  useEffect(() => {
    sound.music(track)

    // Left playing on the way out would put a game's music under the menu.
    return () => sound.music(null)
  }, [sound, track])
}

/**
 * A cue for something that happens once and then stays happened.
 *
 * `when` going from false to true fires it; staying true does not. Which is
 * the whole difficulty of cueing from a view: React will render the same
 * finished round a dozen times, and a game that has been won has been won on
 * every one of them.
 */
export function useCueWhen(when: boolean, cue: Cue): void {
  const sound = useSound()
  const before = useRef<boolean | null>(null)

  useEffect(() => {
    const was = before.current
    before.current = when

    // Arriving somewhere is not becoming it. A game opened onto a round that
    // is already over should not announce a win that happened before she got
    // here — the same rule its two siblings keep, and it did not.
    if (was === null || was === when || !when) return

    sound.play(cue)
  }, [when, cue, sound])
}

/**
 * A cue for something that happens over and over.
 *
 * Keyed on a count rather than on the thing itself, because two card flips in
 * a row are the same view twice and the second one still wants a sound. Never
 * fires on the first render — the count arriving is not the count changing,
 * and a game should not announce a card that was turned before she got here.
 */
export function useCueOnEach(count: number, cue: Cue): void {
  const sound = useSound()
  const last = useRef<number | null>(null)

  useEffect(() => {
    const before = last.current
    last.current = count

    if (before === null || count <= before) return

    sound.play(cue)
  }, [count, cue, sound])
}

/**
 * A cue for something that moved, in either direction.
 *
 * A cursor is a place, not a tally: it goes up and down the row, so "has it
 * increased" is the wrong question and only "is it somewhere else" is the
 * right one. Silent on the first render, because arriving somewhere is not
 * moving there.
 */
export function useCueOnChange(value: number, cue: Cue): void {
  const sound = useSound()
  const last = useRef<number | null>(null)

  useEffect(() => {
    const before = last.current
    last.current = value

    if (before === null || before === value) return

    sound.play(cue)
  }, [value, cue, sound])
}

/** What the three question games have in common, which is most of it. */
export interface AnsweredView {
  readonly correct: number
  readonly wrongAnswers: number
  readonly lastAnswer: { readonly correct: boolean; readonly moreGoes: boolean } | null
}

/**
 * The three answers, decided once for the three games that share them.
 *
 * Monkey's, Turtley's and Rocky's each already share `feedbackFor`, which has
 * exactly these three outcomes. They also each have their own route, each
 * written by copying the last — which is the shape that once had a riddle
 * round saving its skill levels under `trolley`. A wrong cue in one of them
 * would compile, pass, and sound almost right. So it is written here, once.
 *
 * A wrong answer with goes left is an **invitation**: this app has no scores,
 * and "Not quite — try again" must not be met with a buzzer.
 *
 * Counted rather than watched, because `lastAnswer` has no identity of its
 * own — two wrong answers running are an identical object, and only the tally
 * moving says a second one happened.
 */
export function useAnswerCues(view: AnsweredView): void {
  const sound = useSound()
  const answered = view.correct + view.wrongAnswers
  const last = useRef<number | null>(null)

  useEffect(() => {
    const before = last.current
    last.current = answered

    if (before === null || answered <= before) return
    if (!view.lastAnswer) return

    sound.play(
      view.lastAnswer.correct
        ? 'answer.right'
        : view.lastAnswer.moreGoes
          ? 'answer.again'
          : 'answer.moveOn',
    )
    // `view.lastAnswer` deliberately not a dependency: it is read at the moment
    // the tally moves, and its own identity changes on renders where nothing
    // was answered at all.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answered, sound])
}

/**
 * The end of a round, and the one number this app celebrates.
 *
 * Games played — turning up — is the biggest sound in the set, and the only
 * one that is allowed to be a fanfare.
 */
export function useRoundEndCues(finished: boolean, milestone: number | null): void {
  useCueWhen(finished, 'round.done')
  useCueWhen(milestone !== null, 'milestone')
}
